import { useQuery } from "@apollo/client";
import {
  FeaturedCard,
  meGQL,
  productsByTenantGQL,
  Query,
} from "@onmo/graphql/graphql";
import { getCategoryIcon, resolveServiceId } from "../constants/services";
import { useFeaturedCards } from "./useFeaturedCards";
import { useMemo } from "react";
import { HomepageMd } from "../models/homepage";
import { useTgpSubscriptions } from "./useTgpSubscriptions";
import { useAppSelector } from "../components/hooks";

// Types
interface ServiceItem {
  id: string;
  title: string;
  icon: string;
  link: string;
}

interface TenantProduct {
  id: string;
  appUrl: string;
}

interface ServiceConfig {
  title: string;
  usesCustomIcon: boolean;
}

const serviceConfig: Record<string, ServiceConfig> = JSON.parse(
  import.meta.env.VITE_SERVICE_CONFIG ?? "{}",
);

function isFeaturedCard(item: unknown): item is FeaturedCard {
  return (
    item !== null &&
    typeof item === "object" &&
    "metadata" in item &&
    "assets" in item
  );
}

export function useDirectoryData() {
  const { data: dataMe } = useQuery<Query>(meGQL);
  const tenantId = dataMe?.me?.tenantId;
  const { data: productsData } = useQuery<{
    productsByTenant: { items: TenantProduct[] };
  }>(productsByTenantGQL, {
    variables: { input: { tenantId: tenantId ?? "" } },
    skip: !tenantId,
  });
  const { homeFeed } = useFeaturedCards();
  const { theme } = useAppSelector((state) => state.theme);
  const subEnabled = theme?.pages?.directory?.includes("mySubscriptions");
  const { subscriptions: subscriptionData } = useTgpSubscriptions(subEnabled);

  const subscriptions = useMemo((): ServiceItem[] => {
    return (subscriptionData ?? [])
      .map((sub) => {
        const productId = sub.product?.id ?? sub.productId;
        const id = resolveServiceId(productId);
        const link = productsData?.productsByTenant.items.find(
          (product) => product.id === productId,
        )?.appUrl;
        if (!id || !link) return null;

        return {
          id,
          title: sub.product?.title ?? id,
          icon: sub.product?.icon ?? getCategoryIcon(id)?.src3x ?? "",
          link,
        };
      })
      .filter((item): item is ServiceItem => item !== null);
  }, [productsData, subscriptionData]);

  const favoriteItems = dataMe?.me?.metadata?.tgpFavoriteItems ?? [];
  const recentlyPlayedItems = dataMe?.me?.metadata?.tgpRecentlyPlayed ?? [];

  const listService = useMemo((): ServiceItem[] => {
    if (!productsData?.productsByTenant.items.length) {
      return [];
    }

    return productsData.productsByTenant.items.map((product) => {
      const config = serviceConfig[product.id];
      const feedItem = homeFeed?.find(
        (item) =>
          isFeaturedCard(item) && item.metadata.internalProduct === product.id,
      );
      const customIcon = getCategoryIcon(product.id)?.src3x;
      const featuredIcon = feedItem
        ? HomepageMd.getLocalizedAssets(feedItem.assets)?.icon
        : undefined;

      return {
        id: product.id,
        title: config?.title ?? product.id,
        icon: (config?.usesCustomIcon ? customIcon : featuredIcon) ?? "",
        link: product.appUrl,
      };
    });
  }, [homeFeed, productsData]);
  const allGames: FeaturedCard[] = useMemo(() => {
    if (!homeFeed?.length) return [];
    return homeFeed.filter((entry): entry is FeaturedCard => entry !== null);
  }, [homeFeed]);

  return {
    favouriteGames: favoriteItems,
    recentlyPlayed: recentlyPlayedItems,
    listService,
    subscriptions,
    allGames,
  };
}
